import { UiOption } from '@/lib/helpers';
import { PolicyModelV1 } from './policy-v1.model';
import { PolicyAccessList } from '.';
import { PublicServiceMeta, PublicServiceModel } from '../public-service/public-service.model';

type PublicServiceActionUiOption = UiOption & { meta: { level: number } };

export type PublicServiceAction = 'protected' | 'monitored' | 'blocked';

export type PublicServiceBlockAction = 'block' | 'redirect-portal' | 'redirect-url';

export type PublicServiceConfig = PolicyAccessList & {
  monitored?: string[];
  allowPublicServicesByDefault?: boolean;
  blockAction?: PublicServiceBlockAction;
  redirectUrl?: string;
  action: PublicServiceAction;
};

export const PublicServiceActionOptions: PublicServiceActionUiOption[] = [
  {
    label: 'Allow - Monitor',
    value: 'monitored',
    description:
      'All requests to known services will be allowed, and the Allowed Services list will be ignored. In addition, all Sensitive Data controls and settings will be set to "Monitor".',
    meta: {
      level: 1,
    },
  },
  {
    label: 'Allow - Protect',
    value: 'protected',
    description:
      'All requests to known services will be allowed, and the Allowed Services list will be ignored. All Sensitive Data controls and settings will be applied.',
    meta: {
      level: 2,
    },
  },
  {
    label: 'Block',
    value: 'blocked',
    description:
      'All requests to known services will be blocked by default, and individual services can be allowed via the Service Access Control setting. The blocking action will be determined by the Service Block Action setting.',
    meta: {
      level: 3,
    },
  },
];

export const getPublicServiceActionLevel = (action: PublicServiceAction): number => {
  const psOpt = PublicServiceActionOptions.find(({ value }) => value === action);
  return psOpt?.meta.level || 0;
};

export const PublicServiceBlockOptions: UiOption[] = [
  { label: 'Show the no-access page', value: 'block' },
  { label: 'Redirect to portal.sureapth.ai', value: 'redirect-portal' },
  { label: 'Redirect to a URL', value: 'redirect-url' },
];

export const getPublicServiceActionOptions = (
  policy: PolicyModelV1,
  orgPolicy?: PolicyModelV1
): UiOption[] => {
  const options: UiOption[] = [];
  const inheritsFromOrg = policy.inherits();
  const orgActionLevel =
    inheritsFromOrg && orgPolicy ? getPublicServiceActionLevel(orgPolicy.publicServices.action) : 0;

  PublicServiceActionOptions.forEach(({ label, value, description }) => {
    const option: UiOption = {
      label,
      value,
      description,
    };

    if (inheritsFromOrg) {
      const compLevel = getPublicServiceActionLevel(value as PublicServiceAction);
      if (compLevel > orgActionLevel) {
        option.disabled = true;
        option.meta = { blockedByOrgPolicy: true };
      } else if (compLevel === orgActionLevel) {
        option.meta = { orgPolicyLevel: true };
      }
    }

    options.push(option);
  });

  return options;
};

export const syncPublicServiceAction = (policy: PolicyModelV1, orgPolicy: PolicyModelV1) => {
  if (!policy.inherits()) {
    return;
  }

  const orgAction = orgPolicy.publicServices.action;
  const orgActionLevel = getPublicServiceActionLevel(orgAction);
  const compLevel = getPublicServiceActionLevel(policy.publicServices.action);

  if (compLevel > orgActionLevel) {
    policy.publicServices.action = orgAction;
    policy.publicServices.allowPublicServicesByDefault = ['protected', 'monitored'].includes(
      orgAction
    );
  }
};

export type PublicServiceOptionMeta = {
  publicService: PublicServiceModel;
  disabledByAlternateList: boolean;
  enabledByOrgPolicy: boolean;
  monitoredByOrgPolicy: boolean;
  disabledByOrgPolicy: boolean;
};

export const getPublicServiceOptions = (
  publicServices: PublicServiceMeta[],
  aclVariant: 'allow' | 'block',
  policy: PolicyModelV1,
  orgPolicy?: PolicyModelV1
): UiOption[] => {
  const { enabled, disabled } = policy.publicServices;
  const alternateList = aclVariant === 'allow' ? disabled : enabled;

  return publicServices.map((publicService) => {
    const { id, name } = publicService;

    const disabledByAlternateList = alternateList.includes(id);
    const enabledByOrgPolicy = !!orgPolicy?.publicServices?.enabled?.includes(id);
    const disabledByOrgPolicy = !!orgPolicy?.publicServices?.disabled?.includes(id);
    const monitoredByOrgPolicy = !!orgPolicy?.publicServices?.monitored?.includes(id);

    return {
      label: name,
      value: id,
      meta: {
        keyword: name,
        disabledByAlternateList,
        enabledByOrgPolicy,
        monitoredByOrgPolicy,
        disabledByOrgPolicy,
        publicService,
      },
      disabled: enabledByOrgPolicy,
    };
  });
};

export const getNewServiceActionOptions = (): UiOption[] => [
  {
    label: 'Default Service Action',
    value: 'default',
    description:
      'The service will not be added to the allow or block list, and will be subject to the default service action.',
  },
  {
    label: 'Allow - Monitor',
    value: 'monitor',
    description: 'The service will be added to the allow list and set to monitor.',
  },
  {
    label: 'Allow - Protect',
    value: 'allow',
    description: 'The service will be added to the allow list and set to protect.',
  },
  {
    label: 'Block',
    description: 'The service will be added to the block list',
    value: 'block',
  },
];
